import { format } from 'date-fns';

/**
 * Reading a stored date as the calendar day it stands for.
 *
 * Most date columns in this app are Prisma `DateTime`s used as *dates* — an
 * allocation's start, an absence's end. The API serialises them as
 * `2026-08-22T00:00:00.000Z`: a UTC instant standing for a calendar day, not a
 * moment in time. Handing that to `new Date(...)` and then reading or
 * formatting it locally floors it to the **viewer's** local day, so everyone
 * west of Greenwich sees the day before — an allocation ending today groups as
 * "Past" with a pill reading yesterday, an absence starting on the 17th shows
 * as the 16th.
 *
 * The rule is app-wide rather than any one feature's, which is why it lives
 * here: it first bit the project Team tab's allocation grouping and then, in
 * exactly the same shape, the dashboard's "Who's away this week" dates.
 */

// Unanchored at the end on purpose, so it matches the full ISO form the API
// actually sends as well as a bare `YYYY-MM-DD`.
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * The calendar day `value` stands for, as **local midnight** — or `null` when
 * it cannot be read as a date.
 *
 * Take the day digits at face value and rebuild them in local time, rather than
 * letting the runtime resolve an instant and then flooring it. Any consumer
 * that both groups by a date and prints it should use this for both, so the
 * bucket a row lands in always agrees with the date shown on it.
 */
export const calendarDay = (value: string | Date): Date | null => {
  if (typeof value === 'string') {
    const parts = DATE_ONLY.exec(value);
    if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }
  const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

/**
 * A stored date as people read it — "1 Apr 2026" — from its calendar day, so
 * the same in every time zone. An empty string when it cannot be read.
 */
export const formatCalendarDay = (value: string | Date): string => {
  const day = calendarDay(value);
  return day ? format(day, 'd MMM yyyy') : '';
};
